import { BundledProduct, BundledProductContactRecord, MyProfileData } from 'cpfr-common';
import { DefinitionList, DefinitionListItem } from '@cp-shared-19/frontend-ui';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    TranslationFormat,
    useTranslationWithFormatting,
} from '../../../../../localization/useTranslationWithFormatting';
import { RootState } from '../../../../../store/rootReducer';
import { ErrorNotification } from '../../../../notifications/error/ErrorNotification';
import { BundledProductContentSelector } from './BundledProductContentSelector';
import { useTranslation } from 'react-i18next';

type BundledProductComponentProps = {
    isSMECustomer?: boolean;
    details?: BundledProduct;
    contractMileage?: number;
    myProfileData?: MyProfileData;
};

export const BundledProductComponent: React.FC<BundledProductComponentProps> = ({
    isSMECustomer,
    details,
    //contractMileage,
    myProfileData,
}) => {
    const { t: tBundled, f } = useTranslationWithFormatting('bundled-products');
    const { t: tContracts } = useTranslation('contracts');

    const { cmsContent: bundledProductsContactInfo } = useSelector((state: RootState) =>
        BundledProductContentSelector(state),
    );

    if (!details) {
        return null;
    }

    const { name, status, startDate, endDate, supplier } = details;

    const notAvailable = '-';

    const customerType = useMemo<string>(() => myProfileData?.identification?.customerType || '', [myProfileData]);

    const showEndDate = useMemo<boolean>(
        () => ['Retail', 'BIC', 'BA', 'BNC', 'PM'].includes(customerType),
        [customerType],
    );

    const getContactLine = (bundledProductsContactInfo?: BundledProductContactRecord[]) => {
        if (!bundledProductsContactInfo || !name || !supplier) return [];

        const matchedInfo = bundledProductsContactInfo.find(({ supplierName, productNames, customerType }) => {
            const isMatchedSupplierName: boolean = supplier.toLowerCase().includes(supplierName.toLowerCase());
            const isMatchedProductName: boolean = productNames.some((productName) =>
                name.toLowerCase().includes(productName.toLowerCase()),
            );

            const isMatchedCustomerType: boolean = customerType
                ? customerType === (isSMECustomer ? 'SME' : 'retail')
                : true;
            return isMatchedSupplierName && isMatchedProductName && isMatchedCustomerType;
        });

        if (matchedInfo) {
            const { contactName, contactNumber, contactLink, learnMoreLink } = matchedInfo;
            const contactInfoLine = [contactName, contactNumber, contactLink].filter(Boolean).join('\n');

            return [
                {
                    label: tBundled('contact'),
                    value: contactInfoLine ? (
                        <span style={{ whiteSpace: 'break-spaces' }}>{contactInfoLine}</span>
                    ) : (
                        '-'
                    ),
                    testId: 'contact',
                },
                {
                    label: tBundled('coverage'),
                    value: learnMoreLink ? (
                        <a href={learnMoreLink} target="_blank" rel="noopener noreferrer">
                            {tContracts('learnMoreLinkText')}
                        </a>
                    ) : (
                        '-'
                    ),
                    testId: 'coverage',
                },
            ];
        }

        return [];
    };

    const contactLine: DefinitionListItem[] = getContactLine(bundledProductsContactInfo);

    const nameLine: DefinitionListItem[] = [
        {
            label: tBundled('name'),
            value: name || notAvailable,
            testId: 'name',
        },
    ];

    const startDateLine: DefinitionListItem[] = name?.toLowerCase().includes('extension de garantie')
        ? [
              {
                  label: tBundled('start-date'),
                  value: startDate ? f(startDate, TranslationFormat.DATE) : notAvailable,
                  testId: 'start-date',
              },
          ]
        : [];

    const endDateLine: DefinitionListItem[] =
        showEndDate || name?.toLowerCase().includes('extension de garantie')
            ? [
                  {
                      label: tBundled('end-date'),
                      value: endDate ? f(endDate, TranslationFormat.DATE) : notAvailable,
                      testId: 'end-date',
                  },
              ]
            : [];

    // const contractMileageLine: DefinitionListItem[] =
    //     name?.toLowerCase().includes('entretien') || name?.toLowerCase().includes('extension de garantie')
    //         ? [
    //               {
    //                   label: t('contract-mileage'),
    //                   value: contractMileage ? f(contractMileage, TranslationFormat.DISTANCE) : notAvailable,
    //                   testId: 'contract-mileage',
    //               },
    //           ]
    //         : [];

    const supplierLine: DefinitionListItem[] = [
        {
            label: tBundled('supplier'),
            value: supplier || notAvailable,
            testId: 'supplier',
        },
    ];

    const statusLine: DefinitionListItem[] = [
        {
            label: tBundled('status'),
            value: status || notAvailable,
            testId: 'status',
        },
    ];

    const itemList: DefinitionListItem[] = [
        ...nameLine,
        ...statusLine,
        ...supplierLine,
        ...endDateLine,
        ...contactLine,
        //...contractMileageLine,
        ...startDateLine,
    ];

    const isError = !itemList.filter(({ value }) => value !== notAvailable).length;

    return isError ? (
        <ErrorNotification
            testId={'not-found-error'}
            headline={tBundled('error.headline')}
            text={tBundled('error.text')}
        />
    ) : (
        <DefinitionList split={true} list={itemList} />
    );
};
